import { View, useMedia, useWindowDimensions } from 'tamagui'
import { Slider } from '@components/Slider'
import { DefaultSectionTitle } from '@components/Home/DefaultSectionTitle'
import { ICustomProductList, ICustomProductListItem } from '@dtos/MembersConfig'
import { useGetGeneralConfig } from '@hooks/useMembersConfig'
import { useState, useCallback } from 'react'
import { ContinueCard } from '@components/Home/Continue/ContinueCard'
import { ProductCard } from '@components/Home/MyProducts/ProductCard'
import { EnrollmentContentsModal } from '@components/Home/EnrollmentContentsModal'
import { useProductsCustomList } from '@services/api/graphql/productsCustomList'
import { useTranslation } from 'react-i18next'
import { useToastController } from '@tamagui/toast'
import { InView } from '@components/IntersectionObserver'
import { useGetProductUrl } from '@services/api/graphql/productUrl'
import { openWebBrowser } from '@utils/openWebBrowser'
import { useEnrollmentIdsInfo } from '@services/api/graphql/enrollment'
import { productRedirectToEnrollment } from '@utils/productRedirectToEnrollment'
import { useMediaSize } from '@hooks/useMediaSize'
import { useDomain, useHasFeature } from '@hooks/useThemeParams'
import { CARD_DIMENSIONS } from '@constants/productCardDimension'
import { MEMBERS_PAGE_COURSE_CONTENTS } from '@constants/features'
import { CAN_SELL_CONTENT_IN_APP } from '@constants/storeRules'
import {
  CUSTOM_PRODUCT_LIST_PAGE_SIZE,
  nextCustomProductListState,
} from '@utils/customProductListPagination'
import { isEmptyPageError } from '@utils/apolloErrors'
import { useGuardedLoadMore } from '@hooks/useGuardedLoadMore'
import { DirectPurchaseModal } from '@components/Home/CustomProductList/DirectPurchase/DirectPurchaseModal'
import { useUnlockPurchasedItem } from '@components/Home/CustomProductList/DirectPurchase/useUnlockPurchasedItem'
import {
  DirectPurchaseEntry,
  withPurchaseOrigin,
} from '@components/Home/CustomProductList/DirectPurchase/purchaseOrigin'

export function CustomProductListSlider({
  list,
  cardFormat,
}: {
  list: ICustomProductList
  cardFormat: 'horizontal' | 'vertical'
}) {
  const [customListItems, setCustomListItems] = useState<
    ICustomProductListItem[]
  >(list.items)
  const [isHovered, setIsHovered] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  // A primeira página vem no getMembersConfig com o mesmo tamanho de página, então
  // uma primeira página incompleta já prova o fim da lista. Sem isso toda lista
  // com menos de 10 produtos ainda pedia um offset além do fim, que o core só
  // pode responder com `Empty page!`.
  const [reachedEnd, setReachedEnd] = useState(
    list.items.length < CUSTOM_PRODUCT_LIST_PAGE_SIZE,
  )
  const [isLoadingUrl, setIsLoadingUrl] = useState(false)
  const [purchaseItem, setPurchaseItem] =
    useState<ICustomProductListItem | null>(null)
  const { data: generalConfig } = useGetGeneralConfig()
  const { getProductsCustomList } = useProductsCustomList()
  const { getProductUrl } = useGetProductUrl({
    itemId: '',
    productListId: '',
  })
  const { getEnrollmentIdsInfo } = useEnrollmentIdsInfo()
  const media = useMedia()
  const toast = useToastController()
  const { t } = useTranslation('toast')
  const { width: windowWidth } = useWindowDimensions()
  const sizes = useMediaSize()
  const { subdomainURL } = useDomain()
  const hasCourseContentsPage = useHasFeature(MEMBERS_PAGE_COURSE_CONTENTS)
  const isHorizontal = cardFormat === 'horizontal'
  const shouldDisplaySliderControls = media.gtMd && isHovered
  const showProductName = generalConfig?.showProductName ?? false

  const handleLoadMoreError = useCallback(() => {
    toast.show(t('apolloErrors.productsCustomList.title'), {
      message: t('genericRequestError.message'),
      type: 'error',
    })
  }, [toast, t])

  const onLoadMore = useCallback(async () => {
    const { data, error } = await getProductsCustomList({
      variables: {
        id: list.id,
        offset: customListItems.length,
        limit: CUSTOM_PRODUCT_LIST_PAGE_SIZE,
      },
    })
    const { items, reachedEnd: isEnd } = nextCustomProductListState({
      current: customListItems,
      // A lista pode ter deixado de existir entre a primeira página e o
      // loadMore: `customProductLists(id:)` resolve `where(_id: id)` no core, e
      // publicar a área de membros RECRIA a lista de production com _id novo.
      incoming: data?.membersConfig?.customProductLists?.[0]?.items,
      error,
    })
    setReachedEnd(isEnd)
    setCustomListItems(items)

    // Erro transitório (rede, timeout, 5xx) é relançado de propósito: o guard
    // libera o offset no catch, então a próxima rolagem tenta de novo e o aluno
    // vê o toast. `Empty page!` não passa por aqui — ali a lista acabou mesmo.
    if (error && !isEmptyPageError(error)) throw error
  }, [customListItems, getProductsCustomList, list.id])

  // Guard compartilhado: uma request por vez E nenhum offset pedido duas vezes.
  // O gatilho do slider e o InView do modal disparam em rajada, e o offset sai do
  // tamanho da lista — que fica stale entre o fim da request e o re-render.
  const { loadMore: handleLoadMoreContent } = useGuardedLoadMore({
    offset: customListItems.length,
    hasMore: !reachedEnd,
    onLoadMore,
    onError: handleLoadMoreError,
  })

  const containerPadding = media.gtSm ? 48 : 16
  const spaceBetweenCards = media.gtSm ? 20 : 16
  const slidesPerView = () => {
    const orientation = isHorizontal ? 'horizontal' : 'vertical'
    const cardWidth =
      CARD_DIMENSIONS[orientation][sizes] || CARD_DIMENSIONS[orientation].xs
    return (
      (windowWidth - containerPadding) / (cardWidth.width + spaceBetweenCards)
    )
  }

  const handleButtonPress = () => {
    setOpenModal(true)
  }

  const shouldDisplaySeeAllButton = (itemsLength: number) => {
    return itemsLength > slidesPerView()
  }

  const getEnrollmentInfo = useCallback(
    async (enrollmentId: string) => {
      try {
        const { data } = await getEnrollmentIdsInfo({
          variables: { id: enrollmentId },
        })
        if (data) {
          productRedirectToEnrollment({
            enrollmentId: data.enrollment.id.toString(),
            courseId: data.enrollment.schoolProduct.courses?.[0]?.id.toString(),
            courseContentId:
              data.enrollment.schoolProduct.courses?.[0]?.courseModules
                ?.nodes[0]?.courseContents?.nodes[0]?.id,
            pathId: data.enrollment.schoolProduct.path?.id,
            kind: data.enrollment.schoolProduct.courses?.[0]?.kind,
            baseUrl: subdomainURL,
            hasCourseContentsPage,
          })
        }
      } catch (error) {
        toast.show(t('apolloErrors.enrollmentIdsInfo.title'), {
          message: t('genericRequestError.message'),
          type: 'error',
        })
      } finally {
        setIsLoadingUrl(false)
      }
    },
    [getEnrollmentIdsInfo, toast, t, subdomainURL, hasCourseContentsPage],
  )

  const getProductSellUrl = useCallback(
    async (listId: string, itemId: string, entry?: DirectPurchaseEntry) => {
      try {
        setIsLoadingUrl(true)
        const { data, error } = await getProductUrl({
          variables: {
            itemId,
            productListId: listId,
          },
        })
        if (data) {
          const url = data.productUrl
          // Só o checkout aberto pelo painel leva a origem: o link de venda comum é do produtor.
          openWebBrowser(entry ? withPurchaseOrigin(url, entry) : url, true)
        }
        if (error) {
          throw error
        }
      } catch (error) {
        toast.show(t('apolloErrors.productUrl.title'), {
          message: t('genericRequestError.message'),
          type: 'error',
        })
      } finally {
        setIsLoadingUrl(false)
      }
    },
    [getProductUrl, setIsLoadingUrl, toast, t],
  )

  const handleGoToCheckout = useCallback(
    (item: ICustomProductListItem) => {
      setPurchaseItem(null)
      getProductSellUrl(list.id, item.id, 'home_section')
    },
    [getProductSellUrl, list.id],
  )

  const unlock = useUnlockPurchasedItem({
    listId: list.id,
    items: customListItems,
    onUnlocked: setCustomListItems,
  })

  const handlePurchased = useCallback(
    (item: ICustomProductListItem) => {
      setPurchaseItem(null)
      unlock(item)
    },
    [unlock],
  )

  const customProductCards = useCallback(
    (isModalVersion: boolean, items: ICustomProductListItem[]) => {
      return items.map((item: ICustomProductListItem, index: number) => {
        // No app da loja o item bloqueado é só informativo: sem badge de compra,
        // sem link de venda e sem ação no toque (ver `@constants/storeRules`).
        const isLocked = !item.activeForUser
        const displayBuyTag = isLocked && CAN_SELL_CONTENT_IN_APP
        const displayLockTag = isLocked && !CAN_SELL_CONTENT_IN_APP

        const handleCustomOnPress = async () => {
          if (isLoadingUrl) return
          if (item.activeForUser) {
            await getEnrollmentInfo(item.enrollmentId)
            return
          }
          if (!CAN_SELL_CONTENT_IN_APP) return
          if (item.kind === 'direct_purchase' && item.offerId) {
            setPurchaseItem(item)
          } else {
            await getProductSellUrl(list.id, item.id)
          }
        }

        if (isModalVersion) {
          return (
            <>
              {index === items.length - 1 ? (
                <InView
                  key={item.schoolProductId}
                  onChange={(inView: boolean) =>
                    inView && handleLoadMoreContent()
                  }
                >
                  <ContinueCard
                    cardId={item.schoolProductId}
                    showProductName={showProductName}
                    shouldExtendCard={false}
                    productTitle={item.title}
                    isModalVersion={true}
                    customThumbnail={item.logo}
                    displayPlayIcon={false}
                    displayBuyTag={displayBuyTag}
                    displayLockTag={displayLockTag}
                    customOnPress={handleCustomOnPress}
                  />
                </InView>
              ) : (
                <ContinueCard
                  cardId={item.schoolProductId}
                  showProductName={showProductName}
                  key={item.schoolProductId}
                  shouldExtendCard={false}
                  productTitle={item.title}
                  isModalVersion={true}
                  customThumbnail={item.logo}
                  displayPlayIcon={false}
                  displayBuyTag={displayBuyTag}
                  displayLockTag={displayLockTag}
                  customOnPress={handleCustomOnPress}
                />
              )}
            </>
          )
        }

        return (
          <ProductCard
            key={item.schoolProductId}
            hasAuxiliarTitle={showProductName}
            isHorizontal={isHorizontal}
            displayBuyTag={displayBuyTag}
            displayLockTag={displayLockTag}
            hideProgressInformation={true}
            customOnPress={handleCustomOnPress}
            data={{
              id: item.schoolProductId,
              title: item.title,
              logo: item.logo,
              logoVertical: item.logoVertical,
              kind: item.kind,
            }}
          />
        )
      })
    },
    [
      showProductName,
      isHorizontal,
      handleLoadMoreContent,
      list.id,
      getEnrollmentInfo,
      getProductSellUrl,
      isLoadingUrl,
    ],
  )

  if (customListItems.length === 0) return null
  return (
    <View
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
    >
      <Slider
        slidesTotalNumber={customListItems.length}
        slidesPerView={slidesPerView()}
        displaySliderControls={false}
        navigationControlVariation="dots"
        slidesSpacing={spaceBetweenCards}
        contentInset={containerPadding}
        loadMoreContent={handleLoadMoreContent}
        hasMore={!reachedEnd}
        sliderTitle={
          <DefaultSectionTitle
            title={list.title.charAt(0).toUpperCase() + list.title.slice(1)}
            isHoveringSection={isHovered}
            onButtonPress={handleButtonPress}
            showButton={
              shouldDisplaySeeAllButton(customListItems.length) ?? false
            }
          />
        }
        displaySliderControlsHorizontal={shouldDisplaySliderControls}
      >
        {customProductCards(false, customListItems)}
      </Slider>
      <EnrollmentContentsModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        lessonCards={customProductCards(true, customListItems)}
        title={list.title}
      />
      {CAN_SELL_CONTENT_IN_APP && (
        <DirectPurchaseModal
          item={purchaseItem}
          entry="home_section"
          onClose={() => setPurchaseItem(null)}
          onGoToCheckout={handleGoToCheckout}
          onPurchased={handlePurchased}
        />
      )}
    </View>
  )
}
